import React, { useState, useEffect } from 'react';
import {
  FaSearch, FaArrowLeft,
  FaCheckCircle, FaTimesCircle, FaSortAmountDown,
  FaUser, FaPhone, FaDownload, FaShare, FaSpinner
} from 'react-icons/fa';
import { MdSend } from 'react-icons/md';
import './design.css';

import { ecardApi } from '../../api/eCards';
import { paymentApi } from '../../api/payments';
import PaymentModal from '../../modals/payments/PaymentModal/PaymentModal';
import { useAuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { Container } from 'react-bootstrap';

const CardBundleDetails = ({ batch: propBatch, cards: propCards, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { batchId } = useParams();
  const { currentEvent, user } = useAuthContext();

  const [batch, setBatch] = useState(propBatch || location.state?.batch || null);
  const [cards, setCards] = useState([]); // always start empty to fetch latest cards
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  // Payment & Resend states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);
  const [dynamicMnoOptions, setDynamicMnoOptions] = useState([]);
  const [resendTotalAmount, setResendTotalAmount] = useState('0');
  const [isSendingOrInitiating, setIsSendingOrInitiating] = useState(false);

  const [searchTerm, setSearchTerm] = useState(location.state?.searchKeyword || '');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    if (batchId) {
      setLoading(true);
      ecardApi.getBatchDetails(batchId)
        .then(res => {
          if (res.success || res.status === 800) {
            const batchData = Array.isArray(res.data) ? res.data[0] : res.data;
            if (batchData) {
              setBatch({
                batchId: batchData.payment_id || batchId,
                name: batchData.name || batchData.customer_fname || batch?.name || 'Card Batch',
                coupleName: batchData.customer_fname || batch?.coupleName || 'Card Batch',
                eventType: batchData.event_type || '',
                cardCategory: batchData.card_category || '',
                templateImage: batchData.preview_path || batch?.templateImage,
                isPaid: batchData.payment_status === 1 || batch?.isPaid,
                amount: batchData.amount || batch?.amount
              });
              setCards((batchData.cards || []).map(c => ({
                id: c.id,
                cardId: c.id,
                recipientName: c.name,
                recipientPhone: c.phone_number,
                cardNumber: c.card_number,
                cardType: String(c.card_type),
                templateImage: c.card_path,
                thumbnailImage: c.thumbnail_path,
                status: c.sent_status === 1 ? 'sent' : 'unsent'
              })));
            }
          }
        })
        .catch(err => {
          console.error(err);
          toast.error("Failed to load batch details");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);


  /**
   * fetchImageBlob – converts any public image URL to a Blob using a hidden canvas.
   * This bypasses CORS restrictions that block fetch/XHR on cross-origin images.
   * DigitalOcean Spaces public buckets allow crossOrigin image reads.
   */
  const fetchImageBlob = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
          'image/png'
        );
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = url;
    });

  /* ── Download via API ── */
  const handleDownload = async (card) => {
    if (!card.cardId) { toast.error('Card ID not found.'); return; }
    setDownloadingId(card.cardId);
    try {
      await ecardApi.downloadCard(
        card.cardId,
        `harusionline_Card_${card.recipientName || 'Guest'}_${card.cardType === '1' ? 'Single' : card.cardType === '2' ? 'Double' : ''}.png`
      );
      toast.success('Download started!');
    } catch (e) {
      console.error('Download error', e);
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }

  };

  /* ── Share ── */
  const handleShare = async (card) => {
    if (!card.cardId) { toast.error('Card ID not found.'); return; }

    const fallbackUrl = card.templateImage || batch.templateImage || '';
    const textMsg = `Check out this invitation for ${card.recipientName || 'Guest'}!\n\n${fallbackUrl}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Card for ${card.recipientName || 'Guest'}`,
          text: `Check out this invitation for ${card.recipientName || 'Guest'}!`,
          url: fallbackUrl || undefined,
        });
      } else {
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(textMsg)}`, '_blank');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share error:', err);
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(textMsg)}`, '_blank');
      }
    }
  };


  const handleSendBatchCards = async (isResend = false) => {
    const unsent = cards.filter(c => c.status === 'unsent');

    // Case 1: Initial sending of unsent cards for an already paid batch
    if (!isResend && unsent.length > 0) {
      setIsSendingOrInitiating(true);
      try {
        toast.info(`Sending ${unsent.length} unsent card(s)...`);
        const response = await ecardApi.sendCard(batch.batchId);
        handleApiResponse(response);
      } catch (err) {
        console.error('Error sending cards:', err);
        handleApiResponse(err);
      } finally {
        setIsSendingOrInitiating(false);
      }
      return;
    }

    // Case 2: Resending cards (Initiate payment flow)
    setIsSendingOrInitiating(true);
    try {
      toast.info('Fetching resend price & initiating payment...');

      // 1. Fetch resend price (GET api/ecards-resend-price)
      let resendPrice = 250;
      try {
        const priceRes = await ecardApi.getResendPrice();
        const fetched = priceRes?.data?.resend_rate || 
                        priceRes?.data?.resend_price || 
                        priceRes?.data?.price || 
                        priceRes?.data || 
                        priceRes?.resend_rate;
        if (fetched && !isNaN(parseFloat(fetched))) {
          resendPrice = parseFloat(fetched);
        }
      } catch (pErr) {
        console.error('Could not fetch resend price, using default:', pErr);
      }

      // 2. Amount calculation: all cards in batch * ecard resend price
      const totalCards = cards.length > 0 ? cards.length : (batch.totalCards || 1);
      const totalAmount = (totalCards * resendPrice).toFixed(2);
      setResendTotalAmount(totalAmount);

      // 3. Initiate payment payload with template_id set to payment_id of the batch being resent
      const paymentData = {
        service: "ecard_resend",
        amount: totalAmount,
        phone_number: user?.mobile || user?.phone_number || user?.phone || "0000000000",
        customer_name: currentEvent?.client_name || user?.name || 'Admin',
        template_id: batch?.batchId || batchId
      };

      const initiateResponse = await paymentApi.initiatePayment(paymentData);
      const paymentPayload = initiateResponse?.data || initiateResponse;

      if (initiateResponse?.success || initiateResponse?.status === 800 || initiateResponse?.status === 201 || paymentPayload?.payment) {
        const paymentId = paymentPayload?.payment?.id || paymentPayload?.id;
        const options = paymentPayload?.mno_options || [];

        if (!paymentId) throw new Error('Could not retrieve payment ID from initiation response.');

        setCurrentPaymentId(paymentId);
        setDynamicMnoOptions(options);
        setShowPaymentModal(true);
      } else {
        handleApiResponse(initiateResponse);
      }
    } catch (err) {
      console.error('Error initiating resend payment:', err);
      handleApiResponse(err);
    } finally {
      setIsSendingOrInitiating(false);
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <FaSpinner className="fa-spin" size={40} color="#4169e1" />
        <p className="mt-3" style={{ color: '#4169e1', fontWeight: 600 }}>Loading batch details...</p>
      </div>
    );
  }

  if (!batch) {
    return (
      <Container className="text-center py-5">
        <h4>No Batch Details Found</h4>
        <button
          onClick={handleBack}
          style={{
            marginTop: '12px', padding: '10px 24px', background: '#4169e1',
            color: 'white', border: 'none', borderRadius: '30px',
            fontWeight: '600', cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '8px'
          }}
        >
          <FaArrowLeft /> Go Back
        </button>
      </Container>
    );
  }

  const isPaid = batch.isPaid || batch.paymentStatus?.toLowerCase() === 'paid';

  /* ── derived ── */
  const sentCards = cards.filter(c => c.status === 'sent');
  const unsentCards = cards.filter(c => c.status === 'unsent');

  // card_type "1" = Single, "2" = Double
  const filteredCards = cards.filter(card => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    const matchesName = card.recipientName?.toLowerCase().includes(term);
    const matchesNumber = card.cardNumber?.toLowerCase().includes(term);
    const matchesPhone = card.recipientPhone?.includes(term);
    return matchesName || matchesNumber || matchesPhone;
  });

  const sortedCards = [...filteredCards].sort((a, b) => {
    if (sortBy === 'name') return a.recipientName.localeCompare(b.recipientName);
    if (sortBy === 'status') return a.status.localeCompare(b.status);
    if (sortBy === 'single') return (a.cardType === '1' ? -1 : 1) - (b.cardType === '1' ? -1 : 1);
    if (sortBy === 'double') return (a.cardType === '2' ? -1 : 1) - (b.cardType === '2' ? -1 : 1);
    return 0;
  });

  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  };

  /* ════════ RENDER ════════ */
  return (
    <Container>
      <br />    <br />

      {/* ── Sleek Header ── */}
      <div className="position-relative d-flex justify-content-center align-items-center mb-4 mt-2" style={{ minHeight: '44px' }}>
        <button
          onClick={handleBack}
          className="position-absolute start-0"
          style={{
            background: 'transparent', border: 'none', padding: 0,
            display: 'flex', alignItems: 'center', color: '#64748b',
            cursor: 'pointer', transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#4169e1'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
        >
          <FaArrowLeft size={22} />
        </button>

        <div className="d-flex flex-column align-items-center gap-1">
          <div className="d-flex align-items-center gap-3">
            <h1 className="stylish-heading text-center mb-0" style={{ color: '#4169e1', letterSpacing: '3px' }}>
              {batch.name || batch.coupleName}
            </h1>
            <span style={{
              background: isPaid ? '#d1fae5' : '#fee2e2', color: isPaid ? '#065f46' : '#991b1b',
              padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700',
              display: 'inline-flex', alignItems: 'center', gap: '4px'
            }}>
              {isPaid ? <FaCheckCircle size={11} /> : <FaTimesCircle size={11} />}
              {isPaid ? 'Paid' : 'Unpaid'}
            </span>
          </div>
          {(batch.eventType || batch.cardCategory) && (
            <div className="d-flex align-items-center gap-2">
              {batch.eventType && (
                <span style={{
                  background: '#eff6ff', color: '#2563eb',
                  padding: '2px 8px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '600'
                }}>
                  {batch.eventType}
                </span>
              )}
              {batch.cardCategory && (
                <span style={{
                  background: '#f5f3ff', color: '#6d28d9',
                  padding: '2px 8px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '600'
                }}>
                  {batch.cardCategory}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right Action: Send / Resend All */}
        {isPaid && (
          <button
            onClick={() => handleSendBatchCards(unsentCards.length === 0)}
            disabled={isSendingOrInitiating}
            className="position-absolute end-0"
            style={{
              padding: '8px 16px',
              background: unsentCards.length > 0 ? '#10b981' : '#eef2ff',
              color: unsentCards.length > 0 ? 'white' : '#2563eb',
              border: 'none', borderRadius: '20px', fontWeight: '600',
              cursor: isSendingOrInitiating ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center',
              gap: '6px', fontSize: '0.85rem',
              opacity: isSendingOrInitiating ? 0.7 : 1,
              boxShadow: unsentCards.length > 0 ? '0 2px 8px rgba(16,185,129,0.2)' : 'none'
            }}
          >
            {isSendingOrInitiating ? (
              <><FaSpinner className="fa-spin" size={14} /> Processing...</>
            ) : (
              <><MdSend size={14} /> {unsentCards.length > 0 ? 'Send All Cards' : 'Resend Cards'}</>
            )}
          </button>
        )}
      </div>

      {/* ── Unpaid warning ── */}
      {!isPaid && (
        <div className="cbd-unpaid-banner mb-4" style={{ borderRadius: '12px', padding: '12px 20px', background: '#fee2e2', border: '1px solid #fca5a5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#991b1b' }}>
            <FaTimesCircle size={18} />
            <span style={{ fontSize: '0.9rem' }}>This batch is <strong>unpaid</strong>. Pay now to enable sending cards.</span>
          </div>
          <button style={{ padding: '6px 16px', borderRadius: '20px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, fontSize: '0.85rem' }} onClick={() => alert(`Processing payment for ${batch.batchId}`)}>
            Pay Now
          </button>
        </div>
      )}

      {/* ── Controls: Search, Sort, and Stats ── */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div className="d-flex gap-3 w-100" style={{ maxWidth: '450px' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1 }}>
            <FaSearch style={{ position: 'absolute', top: '10px', left: '14px', color: '#94a3b8', fontSize: '0.85rem' }} />
            <input
              type="text"
              placeholder="Search name, phone..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%', padding: '8px 16px 8px 36px', borderRadius: '8px',
                border: '1px solid #e2e8f0', outline: 'none', fontSize: '0.9rem',
                backgroundColor: '#f8fafc', color: '#334155'
              }}
            />
          </div>
          {/* Sort Dropdown */}
          <div style={{ position: 'relative' }}>
            <FaSortAmountDown style={{ position: 'absolute', top: '10px', left: '12px', color: '#94a3b8', fontSize: '0.85rem' }} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 12px 8px 34px', borderRadius: '8px',
                border: '1px solid #e2e8f0', outline: 'none', fontSize: '0.9rem',
                backgroundColor: '#f8fafc', color: '#334155', appearance: 'none',
                minWidth: '110px', cursor: 'pointer'
              }}
            >
              <option value="name">Name</option>
              <option value="status">Status</option>
              <option value="single">Single</option>
              <option value="double">Double</option>
            </select>
          </div>
        </div>

        {/* Stat Pills */}
        <div className="d-flex gap-2 overflow-auto" style={{ whiteSpace: 'nowrap' }}>
          {[
            { label: 'Total', count: batch.totalCards || cards.length, color: '#4169e1', bg: '#eef2ff' },
            { label: 'Sent', count: sentCards.length, color: '#059669', bg: '#d1fae5' },
            { label: 'Unsent', count: unsentCards.length, color: '#dc2626', bg: '#fee2e2' },
            { label: 'Amount', count: `TSH ${(batch.amount || 0).toLocaleString()}`, color: '#d97706', bg: '#fef3c7' }
          ].map(({ label, count, color, bg }, i) => (
            <div key={i} style={{
              padding: '6px 14px', borderRadius: '20px', border: '1px solid #e2e8f0',
              background: '#fff', color: '#64748b', fontSize: '0.85rem', fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              {label}
              <span style={{
                background: bg, color: color, padding: '2px 8px',
                borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700
              }}>
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Card masonry grid ── */}
      {sortedCards.length === 0 ? (
        <div className="cbd-empty">
          <span>📭</span>
          <p>No cards match your search.</p>
        </div>
      ) : (
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {sortedCards.map(card => (
            <div
              key={card.cardId}
              className="card-wrapper position-relative"
            >
              {/* Sent/Unsent tag */}
              <div style={{
                position: 'absolute', top: '10px', right: '10px',
                backgroundColor: card.status === 'sent' ? '#10b981' : '#f59e0b',
                color: 'white', padding: '3px 10px', borderRadius: '20px',
                fontSize: '0.75rem', fontWeight: 'bold', zIndex: 3,
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}>
                {card.status === 'sent' ? <FaCheckCircle size={10} /> : <FaTimesCircle size={10} />}
                {card.status === 'sent' ? 'Sent' : 'Unsent'}
              </div>

              {/* Card number + type tag (price-tab style) */}
              <div className="price-tab d-flex align-items-center gap-2" style={{ fontSize: '0.78rem' }}>
                {card.cardNumber && <span>#{card.cardNumber}</span>}
                {card.cardType === '1' && <span style={{ opacity: 0.85 }}>· Single</span>}
                {card.cardType === '2' && <span style={{ opacity: 0.85 }}>· Double</span>}
              </div>

              <img
                src={card.templateImage || card.thumbnailImage || batch.templateImage || 'https://via.placeholder.com/480x640?text=No+Preview'}
                alt={card.recipientName}
                className="card-img"
                onError={e => {
                  if (e.target.src !== card.thumbnailImage && card.thumbnailImage) {
                    e.target.src = card.thumbnailImage;
                  } else {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/480x640?text=No+Preview';
                  }
                }}
              />

              {/* Info + quick action links below image */}
              <div style={{ padding: '8px 12px 4px' }}>
                <div style={{ fontWeight: '600', fontSize: '0.88rem', color: '#222' }}>
                  <FaUser size={11} style={{ marginRight: 4 }} />
                  {card.recipientName}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '6px' }}>
                  <FaPhone size={11} style={{ marginRight: 4 }} />
                  {card.recipientPhone}
                </div>
                {/* Download | Share */}
                <div style={{ display: 'flex', gap: '10px', borderTop: '1px solid #f0f0f0', paddingTop: '6px', paddingBottom: '8px' }}>
                  <button
                    onClick={e => { e.stopPropagation(); handleDownload(card); }}
                    disabled={downloadingId === card.cardId}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#4169e1', fontSize: '0.78rem', cursor: downloadingId === card.cardId ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '3px', opacity: downloadingId === card.cardId ? 0.6 : 1 }}
                  >
                    {downloadingId === card.cardId
                      ? <><FaSpinner className="fa-spin" size={11} /> Downloading...</>
                      : <><FaDownload size={11} /> Download</>}
                  </button>
                  <span style={{ color: '#ddd' }}>|</span>
                  <button
                    onClick={e => { e.stopPropagation(); handleShare(card); }}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#4169e1', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                  >
                    <FaShare size={11} /> Share
                  </button>
                </div>
              </div>
            </div>
          ))}
        </Masonry>
      )}

      {/* Resend Card Payment Modal */}
      <PaymentModal
        show={showPaymentModal}
        handleClose={() => setShowPaymentModal(false)}
        amount={resendTotalAmount}
        title="Malipo ya Kutuma Tena Kadi (Card Resend Payment)"
        paymentId={currentPaymentId}
        onPaymentSuccess={async () => {
          toast.success("Malipo ya kutuma tena kadi yamekamilika! Inatuma kadi sasa...");
          setShowPaymentModal(false);
          try {
            const sendRes = await ecardApi.sendCard(batch.batchId);
            handleApiResponse(sendRes);
          } catch (sendErr) {
            console.error('Error resending cards after payment:', sendErr);
            handleApiResponse(sendErr);
          }
        }}
        onPaymentError={(err) => {
          toast.error(err.message || "Imeshindwa kukamilisha malipo ya kutuma kadi");
        }}
        mnoOptions={dynamicMnoOptions}
        summaryChildren={
          <div className="payment-summary">
            <div className="payment-summary-item d-flex justify-content-between my-1">
              <span>Jumla ya Kadi za Batch</span>
              <strong>{cards.length}</strong>
            </div>
            <div className="payment-summary-item d-flex justify-content-between my-1">
              <span>Batch ID</span>
              <strong>{batch?.batchId}</strong>
            </div>
          </div>
        }
      />
    </Container>
  );
};

export default CardBundleDetails;